import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import LocationCard from "../Components/LocationCard";
import TenantCard from "../Components/TenantCard";
import AddLocation from "./AddLocation";
import EditLocation from "./EditLocation";
import SelectTenant from "./SelectTenant";
import { useApp } from "../../../../context/AppContext";

import UserService from "../../../../services/UserService";
import { ShowBottomBar, ShowTopBar } from "../../../../services/ToggleNativeBar";
import { getTenantText } from "../../../../utils/translationHelpers";
import { IButtonAction, handleRedirect, setBackButtonVisibility } from "../../../../utils/CommonFunctions";
import Button from "../../../Reusable/Button";

const PROFILE_FRAGMENT = "#referer=profile";

const ChangeLocation = () => {
  const [t] = useTranslation("translations");
  const {setCurrentTenant, settings} = useApp();

  const [locations, setLocations] = useState(UserService.getAttribute("locations"));
  const [tenants, setTenants] = useState(UserService.getAttribute("tenants"));

  const MAX_LOCATIONS = locations.length >= 10;
  const MAX_TENANTS = tenants.length >= 10;

  const history = useNavigate();
  const location = useLocation();

  useEffect(() => {
    ShowTopBar(true);
    ShowBottomBar(false);
    setBackButtonVisibility(true);
  }, []);

  let locationPath = "",
    tenantPath = "",
    referredFromProfile: Boolean;

  if (PROFILE_FRAGMENT === location.hash) {
    // go to edit location page
    referredFromProfile = true;
    locationPath = `/LOCATION_SELECTION/${EditLocation.displayName}`;
    tenantPath = `/LOCATION_SELECTION/${SelectTenant.displayName}`;
  } else {
    // select location page
    referredFromProfile = false;
    locationPath = "/";
    tenantPath = "/";
  }

  function onDeleteLocation(index: number) {
    const newItems = [...locations];
    newItems.splice(index, 1);

    setLocations(newItems);
    UserService.setAttribute("locations", newItems);

    // Removes savedLocationTenant key if a user deletes the current one they have selected
    if (UserService.getAttribute("savedLocationTenant").id === locations[index].id) {
      UserService.removeAttribute("savedLocationTenant");
    }
  }

  function onDeleteTenant(index: number) {
    const newItems = [...tenants];
    newItems.splice(index, 1);

    setTenants(newItems);
    UserService.setAttribute("tenants", newItems);

    // Removes savedLocationTenant key if a user deletes the current one they have selected
    if (UserService.getAttribute("savedLocationTenant").id === tenants[index].id) {
      UserService.removeAttribute("savedLocationTenant");
    }
  }

  const handleAddButtonClick = () => {
    const button_action: IButtonAction = {
      type: "IN_APP",
      url: `${window.location.origin}/LOCATION_SELECTION/${SelectTenant.displayName}`,
    }
    const pageName = "";
    const title = "";
    handleRedirect(button_action, history, title, pageName);
  };

  return (
    <div className="ls__page">
      <Helmet>
        <title>{t("LocationSelection.ChangeLocation.page_title")}</title>
      </Helmet>
      <motion.div className="ls__changesection">
        <p className="ls__pageheader">{t("LocationSelection.ChangeLocation.locations_header", { tenant_plural: getTenantText("tenant_plural") })}</p>
        <>
          {locations.length > 0 ? (
            locations.map(({ distance, id, address, position }, index) => {
              const { postalcode, city } = address;
              return (
                <LocationCard
                  onClick={() => {
                    if (referredFromProfile) {
                      history(`${locationPath}#id=${id}`);
                    } else {
                      UserService.setAttribute("savedLocationTenant", {
                        address,
                        distance,
                        id,
                        position,
                        type: "location",
                      });

                      history(locationPath);
                    }
                  }}
                  main={city}
                  secondary={postalcode}
                  distance={`${distance}km`}
                  key={id}
                  index={index}
                  onDelete={onDeleteLocation}
                />
              );
            })
          ) : (
            <div className="ls__changesection__empty">
              {/* {t("LocationSelection.ChangeLocation.locations--empty")} */}
            </div>
          )}
        </>
        {/* add location button */}
        {/* <button
          onClick={() => {
            history(`/LOCATION_SELECTION/${AddLocation.displayName}`);
          }}
          className={`ls__add ${MAX_LOCATIONS ? "ls__add--disabled" : ""}`}
          disabled={MAX_LOCATIONS}
        >
          {t("LocationSelection.ChangeLocation.add")}
        </button> */}
      </motion.div>
      <motion.div layout className="ls__changesection">
        <p className="">{t("LocationSelection.ChangeLocation.tenants_description1", { tenant_plural: getTenantText("tenant_plural").toLowerCase() })}</p>
        <p className="">{t("LocationSelection.ChangeLocation.tenants_description2", { tenant: getTenantText("tenant").toLowerCase() })}</p>
        {tenants.length > 0 ? (
          tenants.map(({ name, description, image, logo, slug, id, ...rest }, index) => (
            <TenantCard
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                !referredFromProfile &&
                  UserService.setAttribute("savedLocationTenant", {
                    type: "tenant",
                    name,
                    slug,
                    description,
                    image,
                    logo,
                    id,
                    ...rest,
                  });
                const attribute = UserService.getAttribute("savedLocationTenant");
                if ("slug" in attribute && "name" in attribute) {
                  history(`${tenantPath}${attribute.slug.toLowerCase()}`);
                  setCurrentTenant(attribute.slug.toLowerCase());
                } else {
                  // add a case where it routes to a URL instead
                  history("niagara.nav-test.dev.identos.ca")
                }
                
              }}
              main={name}
              secondary={description}
              image={image}
              logo={logo}
              key={id}
              index={index}
              onDelete={onDeleteTenant}           
            />
          ))
        ) : (
          <div className="ls__changesection__empty">
            {/* <p className="ls__changesection__empty">
              {t("LocationSelection.ChangeLocation.tenants--empty-1")}
            </p>
            <p className="ls__changesection__empty">
              {t("LocationSelection.ChangeLocation.tenants--empty-2")}
            </p> */}
          </div>
        )}
        <Button
          buttonType="IN_APP"
          buttonClassname={`ls__add fill-button ${MAX_TENANTS ? "ls__add--disabled" : ""}`}
          buttonText={t("LocationSelection.ChangeLocation.add")}
          handleRedirect={(()=> handleAddButtonClick())}
          showButtonIcon={false}
          buttonIconImage=""
          buttonIconClassname=""
          buttonIconPosition=""
        />
      </motion.div>
    </div>
  );
};

ChangeLocation.displayName = "ChangeLocation";

export default ChangeLocation;